import React, { MouseEventHandler } from "react";
import classNames from "classnames";
import { sizedIconClasses } from "./styles";
import { Link, To } from "react-router-dom";

export interface ButtonProps {
  to?: string | To;
  href?: string;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  form?: string;
  type:
    | "primary"
    | "default"
    | "defaultGray"
    | "ghostOld"
    | "ghost"
    | "danger"
    | "dangerOutline";
  size?: "small" | "medium" | "large" | "largeTallOnMobile";
  target?: "_blank" | "_self" | "_parent" | "_top";
  rel?: string;
  title?: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  RightIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  children?: React.ReactNode;
  className?: string;
  iconClassName?: string;
  disabled?: boolean;
  isActive?: boolean;
  focusDisabled?: boolean;
  submit?: boolean;
  id?: string;
  "data-tooltip-id"?: string;
}

const Button = ({
  to,
  href,
  onClick,
  type,
  Icon,
  RightIcon,
  children,
  target,
  rel,
  title,
  form,
  size = "large",
  className = "",
  iconClassName = "",
  disabled = false,
  isActive = false,
  focusDisabled = false,
  submit = false,
  id,
  ...props
}: ButtonProps) => {
  const cssClasses = classNames(
    styles.base(!!disabled, !!focusDisabled),
    children ? sizeStyles[size] : iconOnlySizeStyles[size],
    styles[type](disabled, focusDisabled, isActive),
    className,
  );

  if (to) {
    return (
      <Link
        id={id}
        to={disabled ? "#" : to}
        className={cssClasses}
        title={title}
        target={target}
        onClick={onClick}
        {...props}
      >
        {Icon && (
          <Icon className={classNames(sizedIconClasses(size), iconClassName)} />
        )}
        {children}
        {RightIcon && (
          <RightIcon
            className={classNames(sizedIconClasses(size), iconClassName)}
          />
        )}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        id={id}
        href={disabled ? "#" : href}
        className={cssClasses}
        title={title}
        target={target}
        rel={rel}
        onClick={onClick}
        {...props}
      >
        {Icon && (
          <Icon className={classNames(sizedIconClasses(size), iconClassName)} />
        )}
        {children}
        {RightIcon && (
          <RightIcon
            className={classNames(sizedIconClasses(size), iconClassName)}
          />
        )}
      </a>
    );
  }

  return (
    <button
      id={id}
      onClick={disabled ? () => {} : onClick}
      form={form}
      className={cssClasses}
      disabled={disabled}
      title={title}
      type={submit ? "submit" : "button"}
      {...props}
    >
      {Icon && (
        <Icon className={classNames(sizedIconClasses(size), iconClassName)} />
      )}
      {children}
      {RightIcon && (
        <RightIcon
          className={classNames(sizedIconClasses(size), iconClassName)}
        />
      )}
    </button>
  );
};

const sizeStyles = {
  small: classNames(
    "tw-py-1",
    "tw-px-2",
    "tw-text-xs",
    "tw-rounded-sm",
    "tw-gap-1",
  ),
  medium: classNames("tw-py-1", "tw-px-2", "tw-rounded-lg", "tw-gap-2"),
  large: classNames("tw-py-2", "tw-px-4", "tw-rounded-lg", "tw-gap-2"),
  largeTallOnMobile: classNames(
    "tw-py-3 md:tw-py-2",
    "tw-px-4",
    "tw-rounded-lg",
    "tw-gap-2",
  ),
};

const iconOnlySizeStyles = {
  small: classNames("tw-p-px", "tw-rounded-sm"),
  medium: classNames("tw-p-px", "tw-rounded-lg"),
  large: classNames("tw-p-2", "tw-rounded-lg"),
  largeTallOnMobile: classNames("tw-p-3 md:tw-p-2", "tw-rounded-lg"),
};

const styles = {
  base: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-group",
      "tw-flex",
      "tw-items-center",
      "tw-text-sm",
      "tw-justify-center",
      "tw-font-bold",
      "tw-border",
      "tw-transition-all",
      "tw-box-border",
      disabled
        ? classNames("tw-opacity-50", "tw-cursor-not-allowed")
        : classNames("tw-cursor-pointer", {
            "focus-visible:tw-border-transparent focus-visible:tw-outline-none focus-visible:tw-ring-[1.5px] focus-visible:tw-ring-inset focus-visible:tw-ring-fay-600":
              !focusDisabled,
          }),
    ),
  primary: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-bg-fay-500",
      "tw-text-white",
      "tw-border-fay-500",
      "tw-stroke-white",
      "tw-fill-white",
      !disabled &&
        classNames(
          "hover:tw-bg-fay-600",
          "active:tw-bg-fay-900",
          "hover:tw-text-white",
          "active:tw-text-white",
          "hover:tw-border-fay-600",
          "active:tw-border-fay-900",
          "hover:tw-stroke-white",
          "active:tw-stroke-white",
          "hover:tw-fill-white",
          "active:tw-fill-white",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-fay-500",
          "focus-visible:tw-text-white",
          "focus-visible:tw-stroke-white",
          "focus-visible:tw-fill-white",
        ),
    ),
  default: (disabled: boolean, focusDisabled: boolean, isActive: boolean) =>
    classNames(
      isActive
        ? classNames(
            "tw-bg-fay-200",
            "tw-text-fay-600",
            "tw-border-fay-300",
            "tw-stroke-fay-500",
            "tw-fill-fay-500",
          )
        : classNames(
            "tw-bg-white",
            "tw-text-neutral-700",
            "tw-border-neutral-200",
            "tw-stroke-neutral-700",
            "tw-fill-neutral-700",
          ),
      !disabled &&
        classNames(
          "hover:tw-bg-fay-100",
          "active:tw-bg-fay-200",
          "hover:tw-text-fay-500",
          "active:tw-text-fay-600",
          "hover:tw-border-fay-200",
          "active:tw-border-fay-300",
          "hover:tw-stroke-fay-500",
          "active:tw-stroke-fay-500",
          "hover:tw-fill-fay-500",
          "active:tw-fill-fay-500",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-white",
          "focus-visible:tw-text-neutral-700",
          "focus-visible:tw-stroke-neutral-700",
          "focus-visible:tw-fill-neutral-700",
        ),
    ),
  defaultGray: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-bg-white",
      "tw-text-neutral-700",
      "tw-border-neutral-200",
      "tw-stroke-neutral-700",
      "tw-fill-neutral-700",
      !disabled &&
        classNames(
          "hover:tw-bg-neutral-200",
          "active:tw-bg-neutral-300",
          "hover:tw-text-neutral-600",
          "active:tw-text-neutral-700",
          "hover:tw-border-neutral-200",
          "active:tw-border-neutral-300",
          "hover:tw-stroke-neutral-600",
          "active:tw-stroke-neutral-600",
          "hover:tw-fill-neutral-600",
          "active:tw-fill-neutral-600",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-white",
          "focus-visible:tw-text-neutral-700",
          "focus-visible:tw-stroke-neutral-700",
          "focus-visible:tw-fill-neutral-700",
        ),
    ),
  ghostOld: (disabled: boolean, focusDisabled: boolean, isActive: boolean) =>
    classNames(
      isActive
        ? classNames(
            "tw-bg-fay-200",
            "tw-text-fay-600",
            "tw-border-fay-200",
            "tw-stroke-fay-500",
            "tw-fill-fay-500",
          )
        : classNames(
            "tw-bg-transparent",
            "tw-text-neutral-700",
            "tw-border-transparent",
            "tw-stroke-neutral-700",
            "tw-fill-neutral-700",
          ),
      !disabled &&
        classNames(
          "hover:tw-bg-fay-100",
          "active:tw-bg-fay-200",
          "hover:tw-text-fay-500",
          "active:tw-text-fay-600",
          "hover:tw-border-fay-100",
          "active:tw-border-fay-200",
          "hover:tw-stroke-fay-500",
          "active:tw-stroke-fay-500",
          "hover:tw-fill-fay-500",
          "active:tw-fill-fay-500",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-transparent",
          "focus-visible:tw-text-neutral-700",
          "focus-visible:tw-stroke-neutral-700",
          "focus-visible:tw-fill-neutral-700",
        ),
    ),
  ghost: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-bg-transparent",
      "tw-text-fay-600",
      "tw-border-transparent",
      "tw-stroke-fay-600",
      "tw-fill-fay-600",
      !disabled &&
        classNames(
          "active:tw-bg-neutral-200",
          "hover:tw-text-fay-700",
          "active:tw-text-fay-700",
          "hover:tw-fill-fay-700",
          "active:tw-fill-fay-700",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-white",
          "focus-visible:tw-border-fay-800",
          "focus-visible:tw-text-fay-500",
          "focus-visible:tw-fill-fay-500",
          "focus-visible:tw-stroke-fay-500",
        ),
    ),
  danger: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-bg-red-600",
      "tw-text-white",
      "tw-border-red-600",
      "tw-stroke-white",
      "tw-fill-white",
      !disabled &&
        classNames(
          "hover:tw-bg-red-700",
          "active:tw-bg-red-800",
          "hover:tw-text-white",
          "active:tw-text-white",
          "hover:tw-border-red-700",
          "active:tw-border-red-800",
          "hover:tw-stroke-white",
          "active:tw-stroke-white",
          "hover:tw-fill-white",
          "active:tw-fill-white",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-red-600",
          "focus-visible:tw-text-white",
          "focus-visible:tw-stroke-white",
          "focus-visible:tw-fill-white",
          "focus-visible:tw-ring-red-800",
        ),
    ),
  dangerOutline: (disabled: boolean, focusDisabled: boolean) =>
    classNames(
      "tw-text-red-500",
      "tw-border-red-200",
      "tw-stroke-red-500",
      "tw-fill-red-500",
      !disabled &&
        classNames(
          "hover:tw-bg-red-500",
          "active:tw-bg-red-600",
          "hover:tw-text-white",
          "active:tw-text-white",
          "hover:tw-border-red-500",
          "active:tw-border-red-600",
          "hover:tw-stroke-white",
          "active:tw-stroke-white",
          "hover:tw-fill-white",
          "active:tw-fill-white",
        ),
      !disabled &&
        !focusDisabled &&
        classNames(
          "focus-visible:tw-bg-red-400",
          "focus-visible:tw-text-white",
          "focus-visible:tw-stroke-white",
          "focus-visible:tw-fill-white",
          "focus-visible:tw-ring-red-800",
        ),
    ),
};

export default Button;
